import { AddressLink } from '../common/AddressLink';
import axios from 'axios';
import React, { useState, useEffect, useRef } from 'react';
import { apiClient, showToast } from '../../utils/apiClient';
import { API } from '../../config';
import { formatDate, formatDateTime, formatNotesWithBullets, getDayOfWeek, formatDateWithDay, getLocationDisplay, getLocationChipColor, getRowAlert, formatDatetimeForInput, parseUTCDatetime, getTodayInAEST, isPrepaymentApproaching, hasAppointments, getAppointmentSummary } from '../../utils/helpers';


const CheckoutStep = ({ cart, customer, total, onBack, onOrderSuccess }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const calculateShipping = (cartItems) => {
    let urnShipping = 0;
    let jewelryShipping = 0;
    let keepsakeShipping = 0;

    const urnCount = cartItems.filter(item => item.category === 'urn').length;
    const jewelryCount = cartItems.filter(item => item.category === 'jewelry').length;
    const keepsakeCount = cartItems.filter(item => item.category === 'keepsake').length;

    if (urnCount > 0) {
      urnShipping = 15 + ((urnCount - 1) * 12);
    }
    if (jewelryCount > 0) {
      jewelryShipping = 8 + ((jewelryCount - 1) * 4);
    }
    if (keepsakeCount > 0) {
      keepsakeShipping = 8 + ((keepsakeCount - 1) * 4);
    }

    return urnShipping + jewelryShipping + keepsakeShipping;
  };

  const handleSubmitOrder = async () => {
    setIsProcessing(true);
    
    try {
      const subtotal = cart.reduce((sum, item) => sum + item.price, 0);
      const shipping = calculateShipping(cart);
      const totalAmount = subtotal + shipping;

      // Prepare order data
      const orderData = {
        customer_id: customer.id || `online-${Date.now()}`,
        customer_name: customer.name,
        customer_email: customer.email,
        shipping_address: `${customer.address}, ${customer.city} ${customer.state} ${customer.postcode}`,
        shipping_postcode: customer.postcode,
        items: cart.map(item => ({
          product_name: item.name,
          product_category: item.category,
          quantity: 1,
          unit_price: item.price,
          total_price: item.price,
          pet_personalization: item.petPersonalization || ''
        })),
        subtotal: subtotal,
        shipping_cost: shipping,
        total_amount: totalAmount,
        pet_personalization: cart.map(item => item.petPersonalization).filter(Boolean).join(', ') || '',
        order_status: 'Pending',
        payment_status: 'Pending' // In real system this would be 'Paid' after payment processing
      };

      // Submit to backend
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/online/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log('Order submitted successfully:', result);
      
      // Trigger success step
      onOrderSuccess({
        orderNumber: result.order?.order_number || `OS-${Date.now()}`,
        orderDate: new Date().toISOString(),
        totalAmount: totalAmount,
        itemCount: cart.length,
        estimatedDelivery: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString() // 5 days from now
      });
      
    } catch (error) {
      console.error('Order submission failed:', error);
      alert(`Failed to submit order: ${error.message}. Please try again.`);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <h3 className="text-xl font-semibold mb-6">Checkout</h3>
      
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-medium text-yellow-800">Payment Processing</h4>
            <p className="text-sm text-yellow-700 mt-1">
              This is a demo system. No actual payment will be processed. 
              In production, this would integrate with Stripe or similar payment processor.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Order Summary */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium mb-4">Final Order Summary</h4>
          <div className="space-y-2">
            {cart.map(item => (
              <div key={item.id} className="flex justify-between text-sm">
                <span>{item.name}</span>
                <span>${item.price}</span>
              </div>
            ))}
            <div className="border-t pt-2 flex justify-between font-semibold">
              <span>Total (inc. shipping):</span>
              <span>${total}</span>
            </div>
          </div>
        </div>

        {/* Customer Details */}
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="font-medium mb-4">Shipping Details</h4>
          <div className="text-sm space-y-1">
            <p><strong>Name:</strong> {customer.name}</p>
            <p><strong>Email:</strong> {customer.email}</p>
            <p><strong>Phone:</strong> {customer.phone}</p>
            <p><strong>Address:</strong> <AddressLink address={customer.address} city={customer.city} state={customer.state} postcode={customer.postcode} testId="checkout-shipping-address-link" /></p>
            <p><strong>City:</strong> {customer.city} {customer.state} {customer.postcode}</p>
          </div>
        </div>
      </div>

      <div className="flex justify-between mt-8">
        <button
          onClick={onBack}
          disabled={isProcessing}
          className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          ← Back to Cart
        </button>
        <button
          onClick={handleSubmitOrder}
          disabled={isProcessing}
          className="px-8 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium disabled:opacity-50"
        >
          {isProcessing ? 'Processing...' : 'Submit Order'}
        </button>
      </div>
    </div>
  );
};

// Order Success Step Component

export default CheckoutStep;
